<template>
  <SubHeading :home="home" :tabs="tabs" :currTab="currTab" @active="handleActive" />

  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text">
          <span>Help</span>
        </span>
        <span class="sba-collection-context">Guides and Version Updates</span>
      </div>
    </div>
  </nav>

  <div class="sba-collection-page-surface is-workbench help-page-surface">
    <div class="container help-page-shell">
      <section class="help-hero sba-slide-in">
        <div>
          <label class="active teal-text">Help Center</label>
          <h5>Guides for your workspace</h5>
          <p>
            See quick tutorials for the modules available to your profile, plus the latest upgrade notes for everyone.
          </p>
        </div>
        <span class="help-version-chip">Version 10</span>
      </section>

      <section class="help-update-card sba-slide-in">
        <div class="help-section-head">
          <div>
            <label class="active teal-text">Latest Upgrade</label>
            <h5>Version 10 Highlights</h5>
          </div>
          <i class="material-icons">new_releases</i>
        </div>

        <div class="help-major-grid">
          <article v-for="item in majorUpdates" :key="item.title" class="help-major-item">
            <span>{{ item.number }}</span>
            <div>
              <h6>{{ item.title }}</h6>
              <p>{{ item.copy }}</p>
            </div>
          </article>
        </div>

        <div class="help-update-list">
          <h6>Also Improved</h6>
          <ul>
            <li v-for="item in otherUpdates" :key="item">
              <i class="material-icons">check</i>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
      </section>

      <section class="help-guide-shell sba-slide-in">
        <div class="help-section-head">
          <div>
            <label class="active teal-text">Available Modules</label>
            <h5>Your Tutorials</h5>
          </div>
          <span>{{ guideCards.length }} modules</span>
        </div>

        <div v-if="guideCards.length" class="help-guide-grid">
          <article v-for="guide in guideCards" :key="guide.key" class="help-guide-card">
            <div class="help-guide-top">
              <span class="help-guide-icon">
                <i class="material-icons">{{ guide.icon }}</i>
              </span>
              <div>
                <h6>{{ guide.title }}</h6>
                <p>{{ guide.summary }}</p>
              </div>
            </div>

            <div class="help-guide-block">
              <strong>Pages</strong>
              <div class="help-page-list">
                <span v-for="page in guide.pages" :key="`${guide.key}-${page.path}`">
                  <b>{{ page.label }}</b>
                  <small>{{ page.path }}</small>
                </span>
              </div>
            </div>

            <div class="help-guide-block">
              <strong>What you can do</strong>
              <ul>
                <li v-for="task in guide.functions" :key="task">
                  <i class="material-icons">arrow_forward</i>
                  <span>{{ task }}</span>
                </li>
              </ul>
            </div>
          </article>
        </div>

        <div v-else class="help-empty-state">
          <i class="material-icons">info_outline</i>
          <p>No module tutorials are available for your current profile yet.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import SubHeading from "@/components/HeaderSubDiv.vue";
import { addModules } from "@/composables/cache/modules";

const majorUpdates = [
  {
    number: "01",
    title: "Installable Mobile and Desktop App Experience",
    copy: "easyHMO now feels more at home on phones, tablets, and desktops, with polished installable app icons, splash assets, and top-bar colors.",
  },
  {
    number: "02",
    title: "Cleaner Claims and Authorization Workflows",
    copy: "Tariff lookup, item selection, review, edit, and delete flows now feel calmer and easier to use.",
  },
  {
    number: "03",
    title: "Smarter Mobile and Tablet Experience",
    copy: "Important work areas now use more of the screen, with better search sheets, actions, and responsive cards.",
  },
  {
    number: "04",
    title: "Better Alerts and Notifications",
    copy: "Pre-authorization notifications are more controlled, with less noise and clearer timing for teams.",
  },
  {
    number: "05",
    title: "Stronger Account Safety",
    copy: "Sign-in, password reset, user deletion, and token cleanup now behave more consistently and securely.",
  },
];

const otherUpdates = [
  "Version 10 adds an in-app Help center with permission-aware tutorials.",
  "Tariff cards now support refresh, clear empty states, and searchable tariff previews.",
  "Claim vetting pages now use softer, more scannable review tables and totals.",
  "Provider, client, claim, authorization, invoice, and enrollee surfaces received focused SBA UI cleanup.",
  "Installable app icons, desktop/mobile app colors, splash assets, and deployment reminders were refreshed.",
  "Login, password reset, and first-time password flows now provide clearer user-facing guidance.",
];

const guideLibrary = {
  supervisor: {
    summary: "Review escalated claim work and approve or reject records assigned for supervisor action.",
    pages: [{ label: "All", path: "/supervisor" }],
    functions: ["Review supervisor claim queues", "Open claim batches", "Approve or reject claims with the right decision trail"],
  },
  hod: {
    summary: "Handle HOD-level claim approvals after earlier review stages are complete.",
    pages: [{ label: "All", path: "/hod" }],
    functions: ["Inspect claim approval queues", "Review provider and claim details", "Complete HOD approval decisions"],
  },
  compliance: {
    summary: "Check invoices and claim visibility before finance or final processing.",
    pages: [{ label: "Invoices", path: "/compliance" }],
    functions: ["Filter invoices by client or status", "Open invoice details", "Review compliance-ready invoice records"],
  },
  finance: {
    summary: "Manage invoice payment visibility and claims payment checks.",
    pages: [{ label: "Finance", path: "/finance" }],
    functions: ["Review invoices and claims", "Inspect payment periods", "Confirm records for finance action"],
  },
  enrollee: {
    summary: "Search, filter, and inspect active enrollee records.",
    pages: [{ label: "All", path: "/enrollee" }],
    functions: ["Search by enrollee details", "Filter by scheme, provider, client, status, and duration", "Open enrollee-linked work safely"],
  },
  auth: {
    summary: "Manage pre-authorization requests from providers and HMO-created authorization work.",
    pages: [
      { label: "All", path: "/auth" },
      { label: "Add", path: "/auth/add" },
      { label: "View", path: "/auth/view/:encounterId" },
    ],
    functions: ["Review diagnosis and requested items", "Check benefits, history, and utilization", "Edit, delete, approve, or reject authorization items"],
  },
  claim: {
    summary: "Manage submitted claims, add manual claims, and vet provider claim batches.",
    pages: [
      { label: "All", path: "/claim" },
      { label: "Add", path: "/claim/add" },
      { label: "View", path: "/claim/view/:clusterId" },
    ],
    functions: ["Filter submitted claim batches", "Add claims on behalf of a provider", "Vet, edit, delete, approve, or reject claim items"],
  },
  private: {
    summary: "Manage HMO client records, schemes, policy periods, and enrollee setup.",
    pages: [
      { label: "All", path: "/private" },
      { label: "Add", path: "/private/add" },
      { label: "View", path: "/private/view/:id" },
    ],
    functions: ["Create and view HMO clients", "Renew policy periods", "Manage client-linked records and status"],
  },
  public: {
    summary: "Manage public scheme records and related enrollee setup.",
    pages: [
      { label: "All", path: "/public" },
      { label: "Add", path: "/public/add/:id" },
    ],
    functions: ["Review scheme records", "Add scheme-linked records", "Track public plan setup"],
  },
  retail: {
    summary: "Manage retail client records and their policy details.",
    pages: [
      { label: "All", path: "/retail" },
      { label: "View", path: "/retail/view/:id" },
    ],
    functions: ["Review retail clients", "Open client profiles", "Track retail policy details"],
  },
  tpa: {
    summary: "Manage TPA and retainer clients.",
    pages: [
      { label: "All", path: "/tpa" },
      { label: "Add", path: "/tpa/add" },
      { label: "View", path: "/tpa/view/:id" },
    ],
    functions: ["Create TPA clients", "Open retainer client records", "Review TPA policy setup"],
  },
  client: {
    summary: "Review all clients across HMO, retail, public, and TPA categories.",
    pages: [
      { label: "All", path: "/client" },
      { label: "View", path: "/client/view/:id" },
    ],
    functions: ["Search clients across categories", "Open consolidated client records", "Move from list to detail quickly"],
  },
  provider: {
    summary: "Manage provider records, profiles, and synchronization with oneProvider where available.",
    pages: [
      { label: "All", path: "/provider" },
      { label: "Add", path: "/provider/add" },
      { label: "Sync", path: "/provider/sync" },
      { label: "View", path: "/provider/view/:id" },
    ],
    functions: ["Create and update providers", "Sync provider records", "Review provider profile and access status"],
  },
  tariffMini: {
    summary: "Review and adjust provider tariff items.",
    pages: [
      { label: "All", path: "/tariff-mini" },
      { label: "View", path: "/tariff-mini/view/:id" },
    ],
    functions: ["Find provider tariff sets", "Inspect item prices", "Review tariff changes"],
  },
  tariffPlus: {
    summary: "Review provider tariffs, add single items, and replace tariff records in bulk.",
    pages: [
      { label: "All", path: "/tariff-plus" },
      { label: "View", path: "/tariff-plus/view/:id" },
    ],
    functions: ["Find provider tariff sets", "Adjust item prices", "Add new items", "Replace provider tariffs in bulk"],
  },
  hospital: {
    summary: "View and manage primary care hospital records.",
    pages: [{ label: "All", path: "/hospital" }],
    functions: ["Browse hospitals", "Inspect primary care facility information", "Support client and enrollee hospital review"],
  },
  report: {
    summary: "Review executive, client, scheme, and utilization insights.",
    pages: [{ label: "Insights", path: "/report" }],
    functions: ["Review executive summaries", "Filter client and scheme insights", "Inspect utilization and high-risk trends"],
  },
  advisory: {
    summary: "Review upcoming invoice advisory records.",
    pages: [{ label: "All", path: "/advisory" }],
    functions: ["View upcoming client invoice periods", "Spot delayed records", "Support proactive client follow-up"],
  },
  adder: {
    summary: "Add dependants to active principals.",
    pages: [{ label: "All", path: "/adder" }],
    functions: ["Find active principals", "Add dependant records", "Keep family structures current"],
  },
  bypass: {
    summary: "Add enrollees to monthly lists while bypassing payment where approved.",
    pages: [{ label: "All", path: "/bypass" }],
    functions: ["Upload or add bypass entries", "Review bypassed enrollee records", "Support controlled exceptions"],
  },
  settings: {
    summary: "Manage application setup areas such as modules, users, tariffs, and plans.",
    pages: [{ label: "Settings", path: "/settings" }],
    functions: ["Manage users and module access", "Review setup controls", "Reset user passwords and maintain account access"],
  },
  encounter: {
    summary: "Create and manage provider-side enrollee encounters and pre-authorization requests.",
    pages: [
      { label: "Encounters", path: "/care/encounter" },
      { label: "Add", path: "/care/encounter/add/:encounterId?" },
    ],
    functions: ["Start an enrollee encounter", "Request pre-authorization", "Track HMO responses"],
  },
  billing: {
    summary: "Prepare and review provider claim submissions.",
    pages: [
      { label: "Billings", path: "/care/billing" },
      { label: "View", path: "/care/billing/view/:clusterId" },
    ],
    functions: ["Review billed claim batches", "Inspect submitted items", "Track provider-side claim status"],
  },
  list: {
    summary: "View active enrollees available to the provider.",
    pages: [{ label: "Lists", path: "/care/list" }],
    functions: ["Search active enrollee lists", "Confirm enrollee eligibility", "Support provider front desk checks"],
  },
  users: {
    summary: "View provider profile credentials.",
    pages: [{ label: "User Profiles", path: "/care/users" }],
    functions: ["Review provider user profiles", "Inspect profile credentials", "Support provider account visibility"],
  },
  members: {
    summary: "View client member records.",
    pages: [{ label: "Members", path: "/company/members" }],
    functions: ["Browse members", "Search member records", "Review membership information"],
  },
  invoices: {
    summary: "View client invoices and invoice periods.",
    pages: [{ label: "Invoices", path: "/company/invoices" }],
    functions: ["Review invoice lists", "Open invoice details", "Track client invoice periods"],
  },
  contract: {
    summary: "View HMO contract information.",
    pages: [{ label: "Contract", path: "/company/contract" }],
    functions: ["Read contract details", "Confirm contract terms", "Support client self-service review"],
  },
  healthcare: {
    summary: "View available healthcare facilities for the client.",
    pages: [{ label: "Healthcare Facilities", path: "/company/healthcare" }],
    functions: ["Browse facilities", "Review primary care options", "Support member hospital guidance"],
  },
  profiles: {
    summary: "View company profile credentials.",
    pages: [{ label: "Profiles", path: "/company/profiles" }],
    functions: ["Review profile information", "Inspect account details", "Support company profile visibility"],
  },
  medical: {
    summary: "View available medical facilities.",
    pages: [{ label: "Medical Facilities", path: "/user/medical" }],
    functions: ["Browse hospitals", "Review available care locations", "Support personal care planning"],
  },
  history: {
    summary: "View treatment history.",
    pages: [{ label: "Medical History", path: "/user/history" }],
    functions: ["Review previous hospital visits", "Check treatment records", "Follow personal utilization history"],
  },
  benefits: {
    summary: "View plan benefits.",
    pages: [{ label: "Benefits", path: "/user/benefits" }],
    functions: ["Review covered benefits", "Check plan access", "Understand available care categories"],
  },
  profile: {
    summary: "View profile information.",
    pages: [{ label: "Profile", path: "/user/profile" }],
    functions: ["Review personal profile", "Check account details", "Confirm member information"],
  },
};

export default {
  name: "HelpHome",
  components: { SubHeading },
  setup() {
    const store = useStore();
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    onMounted(() => {
      document.title = "Help";
      tabs.value.push({ id: 1, name: "Help", title: "All", icon: null, url: "help", hash: null });
      currTab.value = tabs.value[0];
      home.value = tabs.value[0];
    });

    const handleActive = (tab) => {
      currTab.value = tab;
    };

    const userModules = computed(() =>
      Array.isArray(store.state.user?.modules) ? store.state.user.modules : []
    );

    const guideCards = computed(() => {
      const userType = store.state.user?.type;

      return addModules()
        .filter((module) =>
          module.type === userType &&
          module.to !== "help" &&
          userModules.value.includes(module.to)
        )
        .map((module) => {
          const guide = guideLibrary[module.to] || {
            summary: module.description || "Use this workspace module for your assigned tasks.",
            pages: [{ label: module.title, path: `/${module.to}` }],
            functions: ["Open the module", "Review the available records", "Complete assigned actions"],
          };

          return {
            key: module.to,
            icon: module.icon,
            title: module.title,
            ...guide,
          };
        });
    });

    return {
      home,
      tabs,
      currTab,
      handleActive,
      guideCards,
      majorUpdates,
      otherUpdates,
    };
  },
};
</script>

<style scoped>
.help-page-surface {
  padding: 0 0 56px;
}

.help-page-shell {
  margin-top: -14px;
  position: relative;
  z-index: 1;
}

.help-hero,
.help-update-card,
.help-guide-shell {
  background: #ffffff;
  border: 1px solid #c8d9e1;
  border-radius: 8px;
  box-shadow: 0 18px 45px rgba(38, 50, 56, 0.08);
}

.help-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 30px;
  margin: 0 0 22px;
}

.help-hero h5,
.help-section-head h5 {
  margin: 6px 0 0;
  color: #263238;
  font-weight: 700;
}

.help-update-card .help-section-head h5 {
  font-size: 1.32rem;
}

.help-hero p {
  color: #546e7a;
  max-width: 700px;
  margin: 10px 0 0;
  line-height: 1.6;
}

.help-version-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #80cbc4;
  color: #00695c;
  background: #e0f2f1;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 700;
  white-space: nowrap;
}

.help-update-card,
.help-guide-shell {
  padding: 28px 30px;
  margin-bottom: 22px;
}

.help-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #e0e7eb;
}

.help-section-head > i {
  color: #00897b;
  font-size: 32px;
}

.help-section-head > span {
  color: #546e7a;
  font-weight: 700;
}

.help-major-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-top: 22px;
}

.help-major-item {
  border: 1px solid #d7e5eb;
  border-radius: 8px;
  padding: 18px;
  background: #f8fbfc;
}

.help-major-item > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  border-radius: 6px;
  background: #e0f2f1;
  color: #00796b;
  font-weight: 800;
  margin-bottom: 14px;
}

.help-major-item h6,
.help-update-list h6,
.help-guide-card h6 {
  margin: 0;
  color: #263238;
  font-weight: 700;
}

.help-major-item p,
.help-guide-card p {
  color: #546e7a;
  line-height: 1.55;
  margin: 8px 0 0;
}

.help-update-list {
  margin-top: 24px;
}

.help-update-list ul,
.help-guide-block ul {
  margin: 12px 0 0;
  padding: 0;
}

.help-update-list li,
.help-guide-block li {
  list-style: none;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #455a64;
  margin-bottom: 9px;
}

.help-update-list li i,
.help-guide-block li i {
  color: #00897b;
  font-size: 18px;
  margin-top: 1px;
}

.help-guide-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 22px;
}

.help-guide-card {
  border: 1px solid #d7e5eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 22px;
}

.help-guide-top {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 16px;
  align-items: flex-start;
}

.help-guide-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  color: #00897b;
  background: #e0f2f1;
}

.help-guide-block {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #edf2f5;
}

.help-guide-block strong {
  display: block;
  color: #455a64;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 10px;
}

.help-page-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.help-page-list span {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  padding: 9px 12px;
  border: 1px solid #d7e5eb;
  border-radius: 8px;
  background: #f8fbfc;
}

.help-page-list b {
  color: #263238;
  font-size: 0.9rem;
}

.help-page-list small {
  color: #607d8b;
}

.help-empty-state {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  border: 1px solid #d7e5eb;
  border-radius: 8px;
  padding: 22px;
  color: #607d8b;
}

.help-empty-state i {
  color: #00897b;
}

@media (max-width: 992px) {
  .help-major-grid,
  .help-guide-grid {
    grid-template-columns: 1fr;
  }

  .help-hero,
  .help-update-card,
  .help-guide-shell {
    padding: 22px;
  }
}

@media (max-width: 600px) {
  .help-page-surface {
    padding: 0 0 36px;
  }

  .help-page-shell {
    margin-top: -12px;
  }

  .help-hero,
  .help-section-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .help-guide-card {
    padding: 18px;
  }
}
</style>
